import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Volume2, VolumeX, Sparkles, HelpCircle, X, Terminal } from 'lucide-react';
import { toast } from 'sonner';

interface LCARSVoiceAssistantProps {
  onNavigateTab: (tab: string) => void;
  onToggleSimpleMode: (mode?: boolean) => void;
  isSimpleMode: boolean;
  currentUserRank?: string;
  currentUserName?: string;
  nextEventTitle?: string;
  nextEventDate?: string;
}

// Star Trek LCARS Beep Generator using Web Audio API
function playLcarsChirp(frequency: number = 880, duration: number = 0.12) {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(frequency * 1.5, ctx.currentTime + duration);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) {
    // AudioContext blocked by browser policy prior to interaction
  }
}

export default function LCARSVoiceAssistant({
  onNavigateTab,
  onToggleSimpleMode,
  isSimpleMode,
  currentUserRank,
  currentUserName,
  nextEventTitle,
  nextEventDate
}: LCARSVoiceAssistantProps) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [lastResponse, setLastResponse] = useState('');
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef<any>(null);

  // Text-to-Speech LCARS Voice Response
  const speakLcarsResponse = (text: string) => {
    setLastResponse(text);
    if (!window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.05;

    // Pick a natural English voice if available
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => v.lang.startsWith('en') && (v.name.includes('Natural') || v.name.includes('Samantha') || v.name.includes('Google') || v.name.includes('Daniel')));
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    window.speechSynthesis.speak(utterance);
  };

  // Process Recognized Voice Command
  const processVoiceCommand = (rawText: string) => {
    const cmd = rawText.toLowerCase().trim();
    playLcarsChirp(1200, 0.1);

    // Command matching
    if (cmd.includes('event') || cmd.includes('meeting') || cmd.includes('mission') || cmd.includes('calendar')) {
      onNavigateTab('events');
      if (nextEventTitle && nextEventDate) {
        speakLcarsResponse(`Affirmative. Displaying events. Next scheduled mission is ${nextEventTitle} on ${nextEventDate}.`);
      } else {
        speakLcarsResponse('Affirmative. Accessing the mission and event logs for the USS Zavala.');
      }
    } else if (cmd.includes('rank') || cmd.includes('profile') || cmd.includes('point') || cmd.includes('who am i')) {
      onNavigateTab('profile');
      const rankStr = currentUserRank ? `Your current rank is ${currentUserRank}.` : '';
      speakLcarsResponse(`Accessing personnel file for ${currentUserName || 'Crew Member'}. ${rankStr}`);
    } else if (cmd.includes('announcement') || cmd.includes('message') || cmd.includes('chat') || cmd.includes('communication') || cmd.includes('ten forward')) {
      onNavigateTab('comms');
      speakLcarsResponse('Subspace communication channels open. Displaying latest ship announcements.');
    } else if (cmd.includes('simple') || cmd.includes('easy') || cmd.includes('cadet')) {
      onToggleSimpleMode(true);
      speakLcarsResponse('Reconfiguring interface to Easy View for simplified touch navigation.');
    } else if (cmd.includes('classic') || cmd.includes('lcars') || cmd.includes('standard') || cmd.includes('bridge')) {
      onToggleSimpleMode(false);
      speakLcarsResponse('Interface restored to full LCARS tactical mode.');
    } else if (cmd.includes('volunteer') || cmd.includes('shift') || cmd.includes('convention') || cmd.includes('booth')) {
      onNavigateTab('con-schedule');
      speakLcarsResponse('Opening convention shift schedule and volunteer assignment matrix.');
    } else if (cmd.includes('help') || cmd.includes('what can you do') || cmd.includes('command')) {
      setIsCheatSheetOpen(true);
      speakLcarsResponse('USS Zavala computer listening. You may ask about events, your rank, announcements, or request interface changes.');
    } else {
      speakLcarsResponse(`Command received: "${rawText}". I could not recognize that operation. Say "Computer, help" for a list of directives.`);
    }
  };

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionClass) {
      setSpeechSupported(false);
      return;
    }

    const recognition = new SpeechRecognitionClass();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      playLcarsChirp(660, 0.15);
    };

    recognition.onresult = (event: any) => {
      const heard = event.results[0][0].transcript;
      setTranscript(heard);
      processVoiceCommand(heard);
    };

    recognition.onerror = (event: any) => {
      console.warn('[Voice Assistant Error]:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error('Microphone permission denied. Please allow microphone access in browser.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {}
      }
    };
  }, [currentUserRank, currentUserName, nextEventTitle, nextEventDate]);

  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Web Speech recognition is not supported on this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      setTranscript('');
      setLastResponse('');
      try {
        recognitionRef.current?.start();
      } catch (e) {
        recognitionRef.current?.stop();
        setTimeout(() => recognitionRef.current?.start(), 150);
      }
    }
  };

  if (!speechSupported) return null;

  return (
    <>
      {/* Floating LCARS Communicator Button */}
      <div className="fixed bottom-20 md:bottom-6 right-4 z-50 flex flex-col items-end gap-2">
        {/* Active Transcript / Response Overlay Popup */}
        {(isListening || transcript || lastResponse) && (
          <div className="bg-black/90 border-2 border-lcars-gold text-white rounded-2xl p-4 shadow-2xl max-w-xs w-72 space-y-2 backdrop-blur-md animate-in fade-in slide-in-from-bottom-2">
            <div className="flex justify-between items-center border-b border-white/10 pb-2">
              <span className="text-[0.65rem] font-bold font-mono text-lcars-gold uppercase tracking-widest flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                LCARS Voice Interface
              </span>
              <button 
                onClick={() => { setTranscript(''); setLastResponse(''); }}
                className="text-[var(--foreground)]/40 hover:text-[var(--foreground)] text-xs font-bold"
              >
                ✕
              </button>
            </div>

            {isListening ? (
              <div className="py-2 flex items-center gap-2 text-xs text-lcars-blue font-bold animate-pulse">
                <span className="w-2.5 h-2.5 rounded-full bg-lcars-red animate-ping" />
                Listening for "Computer..."
              </div>
            ) : transcript ? (
              <div className="text-xs space-y-1">
                <p className="text-[0.65rem] text-[var(--foreground)]/50 font-mono uppercase">You said:</p>
                <p className="font-bold text-white italic">"{transcript}"</p>
              </div>
            ) : null}

            {lastResponse && (
              <div className="pt-2 border-t border-white/10 text-xs text-lcars-tan">
                <p className="text-[0.65rem] text-[var(--foreground)]/50 font-mono uppercase">Computer response:</p>
                <p className="font-medium mt-0.5">{lastResponse}</p>
              </div>
            )}
          </div>
        )}

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCheatSheetOpen(true)}
            className="w-10 h-10 rounded-full bg-black/80 border border-white/20 text-[var(--foreground)]/70 hover:text-white hover:border-lcars-gold flex items-center justify-center shadow-lg transition-colors"
            title="Voice Commands Help"
          >
            <HelpCircle className="w-5 h-5" />
          </button>

          <button
            onClick={toggleListening}
            className={`px-4 py-3 rounded-full font-bold text-xs uppercase tracking-wider flex items-center gap-2.5 shadow-2xl transition-all ${
              isListening
                ? 'bg-lcars-red text-white ring-4 ring-lcars-red/40 animate-pulse scale-105'
                : isSimpleMode
                ? 'bg-[#1f6feb] text-white hover:bg-[#388bfd]'
                : 'bg-lcars-gold text-black hover:bg-lcars-gold/80'
            }`}
          >
            {isListening ? <Mic className="w-4 h-4 animate-bounce" /> : <Mic className="w-4 h-4" />}
            <span>{isListening ? 'LISTENING...' : 'VOICE COMMAND'}</span>
          </button>
        </div>
      </div>

      {/* Voice Commands Cheat Sheet Modal */}
      {isCheatSheetOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--background)] border-4 border-lcars-gold rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="border-b border-white/10 pb-3 flex justify-between items-center">
              <h3 className="text-lg font-bold text-lcars-gold uppercase tracking-wide flex items-center gap-2">
                <Mic className="w-5 h-5 text-lcars-gold" />
                LCARS Voice Directives
              </h3>
              <button
                onClick={() => setIsCheatSheetOpen(false)}
                className="text-[var(--foreground)]/40 hover:text-[var(--foreground)] font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[var(--foreground)]/80">
              Tap the <strong>Voice Command</strong> button or microphone and speak naturally to the USS Zavala computer. Try these spoken phrases:
            </p>

            <div className="space-y-2 font-mono text-xs">
              <div className="bg-white/5 border border-white/10 p-3 rounded-lg space-y-1">
                <p className="text-lcars-blue font-bold">"Computer, what events are coming up?"</p>
                <p className="text-[0.65rem] text-[var(--foreground)]/50">Navigates to Missions and announces next meeting</p>
              </div>

              <div className="bg-white/5 border border-white/10 p-3 rounded-lg space-y-1">
                <p className="text-lcars-gold font-bold">"Computer, what is my rank?"</p>
                <p className="text-[0.65rem] text-[var(--foreground)]/50">Opens your profile and speaks your rank & points</p>
              </div>

              <div className="bg-white/5 border border-white/10 p-3 rounded-lg space-y-1">
                <p className="text-lcars-tan font-bold">"Computer, read announcements"</p>
                <p className="text-[0.65rem] text-[var(--foreground)]/50">Opens Subspace Comms and Ten Forward</p>
              </div>

              <div className="bg-white/5 border border-white/10 p-3 rounded-lg space-y-1">
                <p className="text-lcars-pink font-bold">"Computer, switch to simple view"</p>
                <p className="text-[0.65rem] text-[var(--foreground)]/50">Toggles Easy View mode for mobile devices</p>
              </div>

              <div className="bg-white/5 border border-white/10 p-3 rounded-lg space-y-1">
                <p className="text-lcars-blue font-bold">"Computer, volunteer shifts"</p>
                <p className="text-[0.65rem] text-[var(--foreground)]/50">Opens convention volunteer shift board</p>
              </div>
            </div>

            <div className="pt-2 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setIsCheatSheetOpen(false)}
                className="lcars-pill bg-lcars-gold text-black font-bold px-6 py-2 text-xs"
              >
                GOT IT
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
